import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CalendarIcon, IdCard, Banknote, MapPin, MessageCircle } from "lucide-react";
import { format, differenceInCalendarDays, addDays } from "date-fns";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, PHP } from "@/lib/api";
import {
  computeVehiclePrice, locationFeeFor, carwashFeeFor, securityDepositFor, addonsFor,
  PICKUP_LOCATIONS, DROPOFF_LOCATIONS, DISCOUNT_TIERS,
} from "@/lib/policy";
import { waLink, waVehicleMessage } from "@/lib/contact";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TIMES = Array.from({ length: 15 }, (_, i) => `${String(i + 6).padStart(2, "0")}:00`);

function DatePick({ label, value, onSelect, disabled, testid }) {
  return (
    <div>
      <Label className="text-foreground/70 text-xs uppercase tracking-wider">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={testid} className="w-full mt-2 px-4 py-3 rounded-lg bg-ink-800/60 border border-gold-500/20 text-left flex items-center gap-3 hover:border-gold-500/50 transition">
            <CalendarIcon className="w-4 h-4 text-gold-400" />
            <span className="text-sm">{format(value, "PPP")}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={value} onSelect={(d) => d && onSelect(d)} disabled={disabled} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TimePick({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-foreground/70 text-xs uppercase tracking-wider">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-2 bg-ink-800/60 border-gold-500/20" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LocationChoice({ title, options, value, onChange, vehicle, prefix }) {
  return (
    <div className="glass-card rounded-2xl p-6" data-testid={`${prefix}-section`}>
      <p className="kicker">{title}</p>
      <RadioGroup value={value} onValueChange={onChange} className="mt-4 grid sm:grid-cols-2 gap-3">
        {options.filter((o) => !(o.carsOnly && vehicle?.type === "motorbike")).map((o) => {
          const fee = locationFeeFor(o.id, vehicle);
          return (
            <label key={o.id} className={`flex items-start gap-3 rounded-xl p-4 cursor-pointer border transition ${value === o.id ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20 hover:border-gold-500/40"}`}>
              <RadioGroupItem value={o.id} className="mt-1" data-testid={`${prefix}-${o.id}`} />
              <div>
                <p className="font-sans-display text-sm text-gold-200 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gold-400" /> {o.label}
                </p>
                <p className="text-xs text-foreground/55 mt-1">
                  {o.id === "el_nido" ? <>El Nido transfer · +{PHP(fee)} ({vehicle?.category})</> : o.sub}
                </p>
              </div>
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export default function Booking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [pickup, setPickup] = useState(addDays(new Date(), 1));
  const [returnD, setReturnD] = useState(addDays(new Date(), 3));
  const [pickupTime, setPickupTime] = useState("09:00");
  const [returnTime, setReturnTime] = useState("09:00");
  const [pickupLoc, setPickupLoc] = useState("ppc_shop");
  const [dropoffLoc, setDropoffLoc] = useState("ppc_shop");
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [carwash, setCarwash] = useState(false);
  const [addons, setAddons] = useState([]);
  const [collateral, setCollateral] = useState("id");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [license, setLicense] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    document.title = "Reserve Your Vehicle | PB-Lux Vehicle Rental Puerto Princesa";
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => navigate("/fleet"));
  }, [id, navigate]);

  const days = useMemo(() => Math.max(1, differenceInCalendarDays(returnD, pickup) || 1), [pickup, returnD]);
  const { fullSubtotal, discountPercent, discountAmount, subtotal, effectiveRate, promo } = computeVehiclePrice(v, days, pickup);
  const pickupFee = v ? locationFeeFor(pickupLoc, v) : 0;
  const dropoffFee = v ? locationFeeFor(dropoffLoc, v) : 0;
  const carwashFee = carwash ? carwashFeeFor(v) : 0;
  const availableAddons = addonsFor(v);
  const addonsFee = availableAddons.filter((a) => addons.includes(a.id)).reduce((s, a) => s + a.fee, 0);
  const depositAmount = securityDepositFor(v);
  const total = subtotal + pickupFee + dropoffFee + carwashFee + addonsFee;
  const reservationFee = effectiveRate;
  const remainingBalance = Math.max(0, total - reservationFee);

  const handleProceed = () => {
    if (!name || !email || !phone) {
      toast.error("Please complete your name, email and mobile/WhatsApp number.");
      return;
    }
    if ((pickupLoc === "ppc_hotel" || pickupLoc === "ppc_airport") && !pickupAddress) {
      toast.error("Please enter your hotel name / arrival details for delivery.");
      return;
    }
    if (dropoffLoc === "ppc_hotel" && !dropoffAddress) {
      toast.error("Please enter the hotel for collection.");
      return;
    }
    const draft = {
      vehicle_id: v.id,
      customer_name: name,
      customer_email: email,
      customer_phone: phone,
      country: country || null,
      drivers_license: license || null,
      pickup_date: pickup.toISOString(),
      return_date: returnD.toISOString(),
      pickup_time: pickupTime,
      return_time: returnTime,
      pickup_location_type: pickupLoc,
      dropoff_location_type: dropoffLoc,
      pickup_address: pickupAddress || null,
      dropoff_address: dropoffAddress || null,
      carwash,
      addons,
      collateral_option: collateral,
      notes,
    };
    sessionStorage.setItem("pblux_booking_draft", JSON.stringify(draft));
    sessionStorage.setItem("pblux_vehicle", JSON.stringify(v));
    navigate(`/checkout/${v.id}`);
  };

  if (!v) return <div className="min-h-screen bg-ink-900"><Navbar /></div>;

  const waMsg = waVehicleMessage(v, {
    pickupDate: format(pickup, "MMM d, yyyy"),
    returnDate: format(returnD, "MMM d, yyyy"),
    pickupLocation: PICKUP_LOCATIONS.find((l) => l.id === pickupLoc)?.label,
    dropoffLocation: DROPOFF_LOCATIONS.find((l) => l.id === dropoffLoc)?.label,
  });

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-28 sm:pt-32 pb-16 noise" data-testid="booking-page">
        <div className="max-w-7xl mx-auto px-6">
          <Link to={`/vehicle/${id}`} className="inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-100 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Vehicle
          </Link>

          <p className="kicker">Step 1 of 2 — Booking Details</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3">Reserve Your Drive</h1>

          <div className="mt-6 rounded-xl p-4 bg-gradient-to-r from-green-500/15 via-gold-500/10 to-green-500/15 border border-green-500/30 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="discount-banner">
            <span className="font-sans-display tracking-wider text-green-300 text-xs uppercase">Save More</span>
            {DISCOUNT_TIERS.map((t) => (
              <span key={t.range} className="text-foreground/80">
                <span className="text-gold-300 font-medium">{t.range}</span> = <span className="text-green-300 font-medium">{t.pct}% off</span>
              </span>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-7">
              {/* Dates & Times */}
              <div className="glass-card rounded-2xl p-6">
                <p className="kicker">Rental Dates & Times</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <DatePick label="Pickup Date" value={pickup} testid="pickup-date-trigger"
                    onSelect={(d) => { setPickup(d); if (d >= returnD) setReturnD(addDays(d, 1)); }}
                    disabled={(d) => d < new Date()} />
                  <DatePick label="Return Date" value={returnD} testid="return-date-trigger"
                    onSelect={setReturnD} disabled={(d) => d <= pickup} />
                  <TimePick label="Pickup Time" value={pickupTime} onChange={setPickupTime} testid="pickup-time" />
                  <TimePick label="Return Time" value={returnTime} onChange={setReturnTime} testid="return-time" />
                </div>
                <p className="text-xs text-foreground/50 mt-3">
                  {days} {days === 1 ? "day" : "days"} rental · Minimum 2 days for outside-city drives
                </p>
              </div>

              {/* Pickup location */}
              <LocationChoice title="Pickup Location" options={PICKUP_LOCATIONS} value={pickupLoc} onChange={setPickupLoc} vehicle={v} prefix="pickuploc" />
              {(pickupLoc === "ppc_hotel" || pickupLoc === "ppc_airport") && (
                <div className="glass-card rounded-2xl p-5 -mt-3">
                  <Label className="text-xs uppercase tracking-wider">{pickupLoc === "ppc_airport" ? "Flight / arrival details" : "Hotel name & address"} *</Label>
                  <Input data-testid="pickup-address" value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)}
                    placeholder={pickupLoc === "ppc_airport" ? "Airline, flight no., arrival time" : "Hotel name, Puerto Princesa"} className="mt-2 bg-ink-800/60 border-gold-500/20" />
                </div>
              )}

              {/* Drop-off location */}
              <LocationChoice title="Return / Drop-off Location" options={DROPOFF_LOCATIONS} value={dropoffLoc} onChange={setDropoffLoc} vehicle={v} prefix="dropoffloc" />
              {dropoffLoc === "ppc_hotel" && (
                <div className="glass-card rounded-2xl p-5 -mt-3">
                  <Label className="text-xs uppercase tracking-wider">Hotel for collection *</Label>
                  <Input data-testid="dropoff-address" value={dropoffAddress} onChange={(e) => setDropoffAddress(e.target.value)}
                    placeholder="Hotel name, Puerto Princesa" className="mt-2 bg-ink-800/60 border-gold-500/20" />
                </div>
              )}
              {(pickupLoc === "el_nido" || dropoffLoc === "el_nido") && (
                <p className="text-[11px] text-foreground/50 -mt-3 px-2">
                  El Nido transfer fees cover our team delivering/retrieving the unit (fee by vehicle model/class, per leg). Your daily rental rate stays the same. Not applied to Puerto Princesa Airport bookings.
                </p>
              )}

              {/* Add-on services */}
              {availableAddons.length > 0 && (
                <div className="glass-card rounded-2xl p-6" data-testid="addons-section">
                  <p className="kicker">Optional Add-ons</p>
                  <div className="mt-4 space-y-3">
                    {availableAddons.map((a) => (
                      <label key={a.id} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addons.includes(a.id)}
                          onChange={(e) => setAddons((prev) => e.target.checked ? [...new Set([...prev, a.id])] : prev.filter((x) => x !== a.id))}
                          data-testid={`addon-${a.id}`}
                          className="w-4 h-4 accent-gold-500"
                        />
                        <span className="text-sm text-foreground/75">{a.label} <span className="text-foreground/50">(+{PHP(a.fee)})</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Carwash */}
              {v.type !== "motorbike" && (
                <label className="glass-card rounded-2xl p-5 flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={carwash} onChange={(e) => setCarwash(e.target.checked)} data-testid="carwash-toggle" className="w-4 h-4 accent-gold-500" />
                  <span className="text-sm text-foreground/75">Add return car wash service <span className="text-foreground/50">(+{PHP(carwashFeeFor(v))})</span></span>
                </label>
              )}

              {/* Security option */}
              <div className="glass-card rounded-2xl p-6" data-testid="collateral-section">
                <p className="kicker">Security Option — Your Choice</p>
                <p className="text-xs text-foreground/55 mt-2">
                  Choose to provide a valid passport as security, subject to verification and approval, or pay a refundable security
                  deposit of ₱3,000 for motorbike rentals or ₱5,000 for car rentals. Deposits are refunded after vehicle return and
                  inspection, subject to the rental agreement.
                </p>
                <RadioGroup value={collateral} onValueChange={setCollateral} className="mt-4 grid sm:grid-cols-2 gap-3">
                  <label className={`flex items-start gap-3 rounded-xl p-4 cursor-pointer border transition ${collateral === "id" ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20"}`}>
                    <RadioGroupItem value="id" className="mt-1" data-testid="collateral-id" />
                    <div>
                      <p className="font-sans-display text-sm text-gold-200 flex items-center gap-2"><IdCard className="w-4 h-4 text-gold-400" /> Passport as Security</p>
                      <p className="text-xs text-foreground/55 mt-1">No cash deposit added · Subject to verification & approval before vehicle release</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 rounded-xl p-4 cursor-pointer border transition ${collateral === "deposit" ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20"}`}>
                    <RadioGroupItem value="deposit" className="mt-1" data-testid="collateral-deposit" />
                    <div>
                      <p className="font-sans-display text-sm text-gold-200 flex items-center gap-2"><Banknote className="w-4 h-4 text-gold-400" /> Refundable Security Deposit</p>
                      <p className="text-xs text-foreground/55 mt-1">{PHP(depositAmount)} refundable after return & inspection · Keep your passport</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {/* Customer info */}
              <div className="glass-card rounded-2xl p-6">
                <p className="kicker">Your Details</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Full Name *</Label>
                    <Input data-testid="customer-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Email *</Label>
                    <Input data-testid="customer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Mobile / WhatsApp *</Label>
                    <Input data-testid="customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+63..." className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Country</Label>
                    <Input data-testid="customer-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Philippines" className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider">Driver's License No. {v.type === "motorbike" ? "(optional)" : ""}</Label>
                    <Input data-testid="customer-license" value={license} onChange={(e) => setLicense(e.target.value)} placeholder="License number (shown on pickup)" className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider">Special Requests / Notes</Label>
                    <Textarea data-testid="customer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Child seat, arrival time, etc." className="mt-2 bg-ink-800/60 border-gold-500/20" />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 glass-card rounded-2xl p-6">
                <div className="flex gap-4">
                  <img src={v.image} alt={v.name} className="w-24 h-24 object-cover rounded-xl" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gold-500/80">{v.brand}</p>
                    <p className="font-display text-xl text-foreground">{v.model}</p>
                    <p className="text-xs text-foreground/55 mt-1">{v.name}</p>
                  </div>
                </div>
                <div className="gold-divider my-5" />
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between text-foreground/75">
                    <span>{PHP(effectiveRate)} × {days} {days === 1 ? "day" : "days"}</span>
                    <span>{PHP(fullSubtotal)}</span>
                  </div>
                  {promo && (
                    <div className="flex justify-between text-green-300" data-testid="booking-promo">
                      <span>{v.promo_label || "Promo rate"}</span>
                      <span className="line-through text-foreground/40">{PHP(v.daily_rate)}/day</span>
                    </div>
                  )}
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-green-300" data-testid="booking-discount">
                      <span>{discountPercent}% duration discount</span>
                      <span>− {PHP(discountAmount)}</span>
                    </div>
                  )}
                  {pickupFee > 0 && (
                    <div className="flex justify-between text-foreground/75" data-testid="booking-pickup-fee">
                      <span>{pickupLoc === "el_nido" ? "El Nido pickup" : "Delivery"}</span>
                      <span>{PHP(pickupFee)}</span>
                    </div>
                  )}
                  {dropoffFee > 0 && (
                    <div className="flex justify-between text-foreground/75" data-testid="booking-dropoff-fee">
                      <span>{dropoffLoc === "el_nido" ? "El Nido drop-off" : "Hotel collection"}</span>
                      <span>{PHP(dropoffFee)}</span>
                    </div>
                  )}
                  {addonsFee > 0 && availableAddons.filter((a) => addons.includes(a.id)).map((a) => (
                    <div key={a.id} className="flex justify-between text-foreground/75" data-testid={`booking-addon-${a.id}`}>
                      <span>{a.label}</span>
                      <span>{PHP(a.fee)}</span>
                    </div>
                  ))}
                  {carwashFee > 0 && (
                    <div className="flex justify-between text-foreground/75">
                      <span>Car wash</span>
                      <span>{PHP(carwashFee)}</span>
                    </div>
                  )}
                </div>
                <div className="gold-divider my-5" />
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-end">
                    <span className="text-xs uppercase tracking-widest text-foreground/55">Total Rental</span>
                    <span className="font-display text-3xl text-gold-300" data-testid="booking-total">{PHP(total)}</span>
                  </div>
                  <div className="flex justify-between text-gold-200" data-testid="booking-reservation-fee">
                    <span>Reservation fee (due now)</span>
                    <span className="font-medium">{PHP(reservationFee)}</span>
                  </div>
                  <div className="flex justify-between text-foreground/70" data-testid="booking-remaining">
                    <span>Remaining balance (on pickup)</span>
                    <span>{PHP(remainingBalance)}</span>
                  </div>
                </div>
                <p className="text-[11px] text-foreground/50 mt-3" data-testid="booking-collateral-note">
                  {collateral === "deposit"
                    ? <>+ Refundable security deposit {PHP(depositAmount)} due on pickup (refunded after return & inspection)</>
                    : <>Security: passport on pickup, subject to verification & approval (no cash deposit)</>}
                </p>
                <button onClick={handleProceed} className="btn-gold w-full mt-6" data-testid="proceed-to-checkout">
                  Review & Pay Reservation Fee <ArrowRight className="w-4 h-4" />
                </button>
                <a href={waLink(waMsg)} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-green-500/40 text-green-300 hover:bg-green-500/15 transition text-sm" data-testid="booking-whatsapp">
                  <MessageCircle className="w-4 h-4" /> Ask First on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
